import { type CellContext } from '@tanstack/react-table';
import classnames from 'classnames';
import { QuotesCodeMapping } from '@/constants/quote/quote';
import { timeColumn, countrySymbolName } from '@/components/atoms/ReactTable/columns';
import { generateStockLinkBySymbol } from '@/utils/links';

type DFormat = {
  [QuotesCodeMapping.SYMBOL]: string;
  [QuotesCodeMapping.TRADE_TIME]: number;
  [QuotesCodeMapping.CHINESE_NAME]: string;
  [QuotesCodeMapping.ENGLISH_NAME]: string;
  [QuotesCodeMapping.BID]: string;
  [QuotesCodeMapping.ASK]: string;
  [QuotesCodeMapping.CHANGE_PERCENT_V2]: string;
  [QuotesCodeMapping.CHANGE_V2]: string;
};

export const quotesColumns = [
  timeColumn,
  countrySymbolName,
  {
    header: () => <div className='w-full text-right'>最新價</div>,
    accessorKey: QuotesCodeMapping.CLOSE.toString(),
    sortingFn: 'alphanumeric',
    cell: (props: CellContext<DFormat, string>) => {
      const percent = props?.row?.original[QuotesCodeMapping.CHANGE_PERCENT_V2] || '';
      const isRise = percent?.includes('+');
      const isFall = percent?.includes('-');

      return (
        <div
          className={classnames('text-right text-base font-bold', {
            'text-[#e03f19]': isRise,
            'text-[#208306]': isFall
          })}
          suppressHydrationWarning
        >
          {props.getValue() || '--'}
        </div>
      );
    }
  },
  {
    accessorKey: QuotesCodeMapping.BID.toString(),
    sortingFn: 'alphanumeric',
    header: () => (
      <div className='w-full text-right'>
        <p>買進</p>
        <p>賣出</p>
      </div>
    ),
    cell: (props: CellContext<DFormat, string>) => {
      const ask = props?.row?.original[QuotesCodeMapping.ASK] || '--';

      return (
        <div className='text-right'>
          <p className='text-[#383838]' suppressHydrationWarning>
            {props.getValue() || '--'}
          </p>
          <p className='text-[#383838]' suppressHydrationWarning>
            {ask}
          </p>
        </div>
      );
    }
  },
  {
    accessorKey: QuotesCodeMapping.CHANGE_PERCENT_V2.toString(),
    sortingFn: 'sortingStringNumberFn',
    header: () => (
      <div className='w-full text-right'>
        <p>變%</p>
        <p>變動</p>
      </div>
    ),
    cell: (props: CellContext<DFormat, string>) => {
      const percent = props.getValue() ? `${props.getValue()}%` : '--';
      const change = props?.row?.original[QuotesCodeMapping.CHANGE_V2];
      const isRise = percent?.includes('+');
      const isFall = percent?.includes('-');

      return (
        <span
          className={classnames('text-right text-sm', {
            'text-[#e03f19]': isRise,
            'text-[#208306]': isFall
          })}
          suppressHydrationWarning
        >
          <p className='font-bold' suppressHydrationWarning>
            {percent}
          </p>
          <p suppressHydrationWarning>{change}</p>
        </span>
      );
    }
  }
];

export const performanceColumns = [
  timeColumn,
  countrySymbolName,
  {
    header: () => <div className='w-full text-right'>週漲幅</div>,
    sortingFn: 'sortingStringNumberFn',
    accessorKey: QuotesCodeMapping.WEEK_CHANGE_PERCENT.toString(),
    cell: (props: CellContext<DFormat, string>) => {
      const percent = props.getValue() ? `${props.getValue()}%` : '--';
      const isRise = percent?.includes('+');
      const isFall = percent?.includes('-');

      return (
        <div
          className={classnames('text-right text-sm', {
            'text-[#e03f19]': isRise,
            'text-[#208306]': isFall
          })}
        >
          {percent}
        </div>
      );
    }
  },
  {
    header: () => <div className='w-full text-right'>月漲幅</div>,
    sortingFn: 'sortingStringNumberFn',
    accessorKey: QuotesCodeMapping.MONTH_CHANGE_PERCENT.toString(),
    cell: (props: CellContext<DFormat, string>) => {
      const percent = props.getValue() ? `${props.getValue()}%` : '--';
      const isRise = percent?.includes('+');
      const isFall = percent?.includes('-');

      return (
        <div
          className={classnames('text-right text-sm', {
            'text-[#e03f19]': isRise,
            'text-[#208306]': isFall
          })}
        >
          {percent}
        </div>
      );
    }
  },
  {
    header: () => <div className='w-full text-right'>年至今</div>,
    sortingFn: 'sortingStringNumberFn',
    accessorKey: QuotesCodeMapping.YEAR_CHANGE_PERCENT.toString(),
    cell: (props: CellContext<DFormat, string>) => {
      const percent = props.getValue() ? `${props.getValue()}%` : '--';
      const isRise = percent?.includes('+');
      const isFall = percent?.includes('-');

      return (
        <div
          className={classnames('text-right text-sm', {
            'text-[#e03f19]': isRise,
            'text-[#208306]': isFall
          })}
        >
          {percent}
        </div>
      );
    }
  }
];

export const mobileQuotesColumns = [
  timeColumn,
  {
    header: () => <div className='text-left'>名稱</div>,
    accessorKey: QuotesCodeMapping.CHINESE_NAME.toString(),
    cell: (props: CellContext<DFormat, string>) => {
      const englishName = props?.row?.original?.[QuotesCodeMapping.ENGLISH_NAME];

      return (
        <div className='text-left '>
          <span className='font-semibold leading-[14px] text-[#383838]'>{props.getValue()}</span>
          <p className='mt-[6px] text-xs leading-3 text-[#848d97]'>{englishName}</p>
        </div>
      );
    }
  },
  {
    header: () => <div className='w-full text-right'>最新價</div>,
    accessorKey: QuotesCodeMapping.CLOSE.toString(),
    cell: (props: CellContext<DFormat, string>) => {
      const percent = props?.row?.original[QuotesCodeMapping.CHANGE_PERCENT_V2] || '';
      const isRise = percent?.includes('+');
      const isFall = percent?.includes('-');

      return (
        <div
          className={classnames('text-right text-base font-bold', {
            'text-[#e03f19]': isRise,
            'text-[#208306]': isFall
          })}
        >
          {props.getValue() || '--'}
        </div>
      );
    }
  },
  {
    accessorKey: QuotesCodeMapping.BID.toString(),
    header: () => (
      <div className='w-full min-w-[45px] text-right'>
        <p>買進</p>
        <p>賣出</p>
      </div>
    ),
    cell: (props: CellContext<DFormat, string>) => {
      const ask = props?.row?.original[QuotesCodeMapping.ASK] || '--';

      return (
        <div className='text-right'>
          <p className='text-[#383838]'>{props.getValue() || '--'}</p>
          <p className='text-[#383838]'>{ask}</p>
        </div>
      );
    }
  },
  {
    accessorKey: QuotesCodeMapping.CHANGE_PERCENT_V2.toString(),
    sortingFn: 'sortingStringNumberFn',
    header: () => (
      <div className='w-[55px] text-right md:w-full'>
        <p>變%</p>
        <p>變動</p>
      </div>
    ),
    cell: (props: CellContext<DFormat, string>) => {
      const percent = props.getValue() ? `${props.getValue()}%` : '--';
      const change = props?.row?.original[QuotesCodeMapping.CHANGE_V2];
      const isRise = percent?.includes('+');
      const isFall = percent?.includes('-');

      return (
        <span
          className={classnames('text-right text-sm', {
            'text-[#e03f19]': isRise,
            'text-[#208306]': isFall
          })}
        >
          <p className='font-bold'>{percent}</p>
          <p>{change}</p>
        </span>
      );
    }
  }
];

export const mobilePerformanceColumns = [
  timeColumn,
  {
    header: () => <div className='text-left'>名稱</div>,
    accessorKey: QuotesCodeMapping.CHINESE_NAME.toString(),
    cell: (props: CellContext<DFormat, string>) => {
      const englishName = props?.row?.original?.[QuotesCodeMapping.ENGLISH_NAME];
      const symbol = props?.row?.original?.[QuotesCodeMapping.SYMBOL];
      const link = generateStockLinkBySymbol(symbol);

      return (
        <div className='text-left'>
          <a className='font-semibold leading-[14px] text-[#383838]' href={link}>
            {props.getValue()}
          </a>
          <p className='mt-[6px] text-xs leading-3 text-[#848d97]'>{englishName}</p>
        </div>
      );
    }
  },
  {
    header: () => <div className='w-full text-right'>週漲幅</div>,
    sortingFn: 'sortingStringNumberFn',
    accessorKey: QuotesCodeMapping.WEEK_CHANGE_PERCENT.toString(),
    cell: (props: CellContext<DFormat, string>) => {
      const percent = props.getValue() ? `${props.getValue()}%` : '--';
      const isRise = percent?.includes('+');
      const isFall = percent?.includes('-');

      return (
        <div
          className={classnames('text-right text-sm', {
            'text-[#e03f19]': isRise,
            'text-[#208306]': isFall
          })}
        >
          {percent}
        </div>
      );
    }
  },
  {
    header: () => <div className='w-full text-right'>月漲幅</div>,
    sortingFn: 'sortingStringNumberFn',
    accessorKey: QuotesCodeMapping.MONTH_CHANGE_PERCENT.toString(),
    cell: (props: CellContext<DFormat, string>) => {
      const percent = props.getValue() ? `${props.getValue()}%` : '--';
      const isRise = percent?.includes('+');
      const isFall = percent?.includes('-');

      return (
        <div
          className={classnames('text-right text-sm', {
            'text-[#e03f19]': isRise,
            'text-[#208306]': isFall
          })}
        >
          {percent}
        </div>
      );
    }
  },
  {
    header: () => <div className='w-full text-right'>年至今</div>,
    sortingFn: 'sortingStringNumberFn',
    accessorKey: QuotesCodeMapping.YEAR_CHANGE_PERCENT.toString(),
    cell: (props: CellContext<DFormat, string>) => {
      const percent = props.getValue() ? `${props.getValue()}%` : '--';
      const isRise = percent?.includes('+');
      const isFall = percent?.includes('-');

      return (
        <div
          className={classnames('text-right text-sm', {
            'text-[#e03f19]': isRise,
            'text-[#208306]': isFall
          })}
        >
          {percent}
        </div>
      );
    }
  }
];
